import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import AppTextField from "../../../../../components/common/forms/AppTextField";
import AppDatePicker from "../../../../../components/common/forms/AppDatePicker";
import AppSelect from "../../../../../components/common/forms/AppSelect";
import { useGetEquipmentsQuery } from "../../../../../hooks/useEquipments";
import { useGetLocationsQuery } from "../../../../../hooks/useLocations";
import { useGetOperatorsQuery } from '../../../../../hooks/useOperators';
import { useGetAssigneesQuery } from "../../../../../hooks/useAssignees";
import { appColors } from "../../../../../theme";
import { useEJOESRApprovalContext } from "../Context/EJOESRApprovalContext";
import { useESRApprovalContext } from "../Context/ESRApprovalContext";
import {
  EJOManagementFieldsApproval,
  EJORequestBodyApproval,
} from "./utils/types";
import { ESRRequestBody } from "../ESR/utils/types";
import {
  EJOManagementInitialValues,
  EJOManagementSchema,
} from "./utils/schema";
import {
  useGetCurrentEJOSelected,
  useUpdateEJO,
} from "../../../../../hooks/useEJOApproval";
import { useCreateESR } from "../../../../../hooks/useESRApproval";
import { Box, Grid, Switch, Typography } from "@mui/material";
import EJOSRTable from "./EJO-SR/SRTable";
import EJOSRManagementForm from "./EJO-SR/SRForm";
import { useAuth } from "../../../../../components/AuthProvider";
import EJOConfirmDialog from "./EJOConfirmDialog";
import EJOSRConfirmDeleteDialog from "./EJO-SR/EJOSRConfirmDeleteDialog";
import { useSnackBar } from "../../../../../components/ToastProvider";
import { EJOSRRequestBodyApproval } from "./EJO-SR/utils/types";
import LoadingIndicator from "../../../../../components/common/LoadingIndicator";
import { cleanDigitSectionValue } from "@mui/x-date-pickers/internals/hooks/useField/useField.utils";

const EJOApprovalForm = () => {
  const { id: paramsId } = useParams();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const referrer = location.state?.referrer;

  const currUser = useAuth();
  const rolePermissions = currUser && currUser.currentUserRole?.permissions;
  const [openClearSrDialog, setOpenClearSrDialog] = useState<boolean>(false);

  const { data: ejo, isLoading: isEJOLoading } =
    useGetCurrentEJOSelected(paramsId);
  const updateEJO = useUpdateEJO();
  const createESR = useCreateESR();
  const equipmentData = useGetEquipmentsQuery();
  const locationData = useGetLocationsQuery();
  const operatorData = useGetOperatorsQuery();
  const picData = useGetAssigneesQuery();

  const {
    serviceRequestsArray,
    setServiceRequestsArray,
    currentEJOSelected,
    setCurrentEJOSelected,
    isViewOnly,
    setViewOnly,
    currentSRSelected
  } = useEJOESRApprovalContext();
  const { esrArray, setEsrArray } = useESRApprovalContext();

  const equipmentOptions: any[] =
    equipmentData?.data === undefined
      ? []
      : equipmentData.data.map((e) => {
          return {
            id: e.id,
            label: e.equipmentNo,
            value: e.id,
          };
        });

  const locationOptions: any[] =
    locationData?.data === undefined
      ? []
      : locationData.data.map((e) => {
          return {
            id: e.id,
            label: e.name,
            value: e.id,
          };
        });

  const operatorOptions: any[] =
    operatorData?.data === undefined
      ? []
      : operatorData.data.map((e) => {
        return {
          id: e.id,
          label: e.operatorName,
          value: e.id,
        };
      });

  const picOptions: any[] =
    picData?.data === undefined
      ? []
      : picData.data.map((e) => {
          return {
            id: e.id,
            label: e.name,
            value: e.id,
          };
        });

  const {
    register,
    control,
    setValue,
    getValues,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EJOManagementFieldsApproval>({
    defaultValues: EJOManagementInitialValues,
    mode: "onChange",
    resolver: yupResolver(EJOManagementSchema),
  });

  const [currentEquipmentId, setCurrentEquipmentId] = useState<string>(
    watch("equipmentId")
  );

  const equipmentModel = useMemo(() => {
    if (!equipmentData.data) {
      return;
    }

    const foundEquipment = equipmentData.data.find(
      (i) => watch("equipmentId") == i.id
    );

    return foundEquipment?.model;
  }, [watch("equipmentId"), equipmentData]);

  useEffect(() => {
    setViewOnly(checkViewOnly() ?? true);
    setServiceRequestsArray([]);
  }, []);

  useEffect(() => {
    setCurrentEJOSelected(ejo ?? currentEJOSelected);
  }, [ejo]);

  useEffect(() => {
    if (!referrer) {
      setEsrArray([]);
    }
  }, [referrer]);

  useEffect(() => {
    //* If both the current selected and the recent value is same
    if (currentEquipmentId == watch("equipmentId")) {
      return;
    }

    //* Use-case: Create, when the currentEquipmentId is empty
    if (!currentEquipmentId) {
      setCurrentEquipmentId(watch("equipmentId"));
    }

    if (serviceRequestsArray.length != 0) {
      if (!isViewOnly) {
        setOpenClearSrDialog(true);
      }
    }
  }, [watch("equipmentId")]);

  useEffect(() => {
    if (paramsId) {
      if (ejo) {
        setValue("isOperable", ejo.isOperable);
        setValue("dateNeeded", ejo.dateNeeded);
        setValue("smrMileage", ejo.smrMileage);
        setValue("equipmentId", ejo.equipmentId);
        setValue("assigneeId", ejo.assigneeId ?? "");
        setValue("projectLocationId", ejo.projectLocationId);
        setValue("operatorId", ejo.operatorId);
        setValue("serviceRequestList", ejo.serviceRequestList);
        setCurrentEquipmentId(ejo.equipmentId);
        setServiceRequestsArray(ejo.serviceRequestList);
      }
    }
  }, [ejo, paramsId, setValue]);

  const onSubmit = (ejoData: EJOManagementFieldsApproval) => {
    function formatDate(date: Date) {
      // Extract date and time components
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      const milliseconds = String(date.getMilliseconds()).padStart(3, "0");

      // Extract timezone offset
      const timezoneOffset = date.getTimezoneOffset();
      const sign = timezoneOffset > 0 ? "-" : "+";
      const absOffset = Math.abs(timezoneOffset);
      const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, "0");
      const offsetMinutes = String(absOffset % 60).padStart(2, "0");

      // Format offset
      const offsetString = `${sign}${offsetHours}${offsetMinutes}`;

      // Format final date string
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}${offsetString}`;
    }

    const formattedDateNeeded = formatDate(new Date(ejoData.dateNeeded));
    const formattedSRList: EJOSRRequestBodyApproval[] =
      serviceRequestsArray.map((sr) => {
        const isExistingInDb = sr.id.length > 5;

        if (!isExistingInDb) {
          return {
            pmCodeId: sr.pmCodeId,
            serviceRequestTypeId: sr.serviceRequestTypeId,
            remarks: sr.remarks,
            id: "",
          ...(sr?.images?.length && {images: sr.images}),
          };
        }

        const deleted = [];
        
        const originalSr = ejo?.serviceRequestList.find(osr => osr.id === sr.id);

        for (const image of originalSr?.images ?? []) {
          if (image && !(sr?.images ?? []).includes(image)) {
            deleted.push(image);
          }
        }

        return {
          pmCodeId: sr.pmCodeId,
          serviceRequestTypeId: sr.serviceRequestTypeId,
          remarks: sr.remarks,
          id: sr.id,
          ...(deleted.length && {deleted}),
          ...(sr?.images?.length && {images: sr.images}),
        };
      });

    const formattedEJOData: EJORequestBodyApproval = {
      isOperable: Boolean(ejoData.isOperable),
      dateNeeded: formattedDateNeeded,
      smrMileage: Number(ejoData.smrMileage),
      equipmentId: ejoData.equipmentId,
      projectLocationId: ejoData.projectLocationId,
      operatorId: ejoData.operatorId,
      assigneeId: ejoData.assigneeId,
      serviceRequestList: formattedSRList,
      status: "Approved",
    }

    updateEJO.mutate(formattedEJOData);
  };

  const onSubmitESR = async () => {
    if (serviceRequestsArray.length != esrArray.length) {
      showSnackBar("All JO must be created first", "error");
      return;
    }

    for (const item of esrArray) {
      const serviceRequest = serviceRequestsArray.find(
        (sr) => item.id == sr.id
      );

      const formattedESRManHourList = item.esrManHourList.map((mh: any) => {
        const isExistingInDb = mh.id.length > 5;

        const { mechanicName, ...newMh } = mh;

        if (!isExistingInDb) {
          const { id, ...rest } = newMh;
          return rest;
        }

        return newMh;
      });

      const formattedESREquipmentServiceList = item.equipmentServiceList.map(
        (es: any) => {
          const isExistingInDb = es.id.length > 5;

          const otherProps = {
            intervalType: "Periodic",
            partsDescriptionId: "",
          };

          const newEsWithOtherProps = { ...es, ...otherProps };

          const { taskTypeItem, ...newEs } = newEsWithOtherProps;

          if (!isExistingInDb) {
            const { id, ...rest } = newEs;
            return rest;
          }

          return newEs;
        }
      );

      const formattedESRData: ESRRequestBody = {
        dateNeeded: currentEJOSelected.dateNeeded as string,
        ejoId: paramsId ?? "",
        equipmentId: currentEJOSelected.equipmentId,
        isWarrantyJob: Boolean(item.isWarrantyJob),
        serviceRequestId: serviceRequest?.id ?? "",
        serviceRequestTypeId: serviceRequest?.serviceRequestTypeId ?? "",
        esrManHourList: formattedESRManHourList,
        equipmentServiceList: formattedESREquipmentServiceList,
        esrNotes: item.esrNotes,
        businessUnitId: currentEJOSelected.businessUnitId,
        images: item.images,
      };
      await createESR.mutateAsync(formattedESRData);
    }

    setTimeout(() => {
      navigate("/esr-management/ejo-approved-and-esr-uncreated", {
        replace: true,
      });
    }, 400);
  };

  const onProceedEquipmentAction = (): void => {
    setCurrentEquipmentId(watch("equipmentId"));
    setServiceRequestsArray([]);
    setOpenClearSrDialog(false);
  };

  const onCancelEquipmentAction = (): void => {
    setValue("equipmentId", currentEquipmentId);
    setOpenClearSrDialog(false);
  };

  const onError = (err: typeof errors) => {
    console.error(err);
  };

  const backTo = () => {
    //* To clear the fields
    setServiceRequestsArray([]);
    setCurrentEJOSelected({
      dateCreated: Date(),
      dateNeeded: Date(),
      projectLocationId: "",
      equipmentId: "",
      operatorId: "",
      assigneeId: "",
      smrMileage: 0,
      isOperable: false,
      serviceRequestList: [],
    });
    navigate(`/${pathSegments[0]}/${pathSegments[1]}`);
  };

  const checkViewOnly = () => {
    return !(
      rolePermissions?.includes("EJO::ESR::APPROVAL") &&
      pathSegments[1]?.includes("ejo-pending")
    );
  };

  //* Used for validating. Did not use isValid because isValid still return false even though the data is valid
  const hasErrors = Object.keys(errors).length > 0;

  if (isEJOLoading) {
    if (paramsId) {
      return <LoadingIndicator show />;
    }
  }

  return (
    <>
      <Stack direction="row" gap={2} justifyContent={"flex-end"} mb={1}>
        <Button
          sx={{ backgroundColor: appColors.lightGray, color: "black" }}
          onClick={backTo}
          disabled={updateEJO.isLoading || createESR.isLoading}
        >
          {(rolePermissions?.includes("EJO::ESR::APPROVAL") &&
            pathSegments[1]?.includes("ejo-pending")) ||
          (rolePermissions?.includes("ESR::MANAGEMENT") &&
            pathSegments[1]?.includes("ejo-approved-and-esr-uncreated"))
            ? "Cancel"
            : "Back"}
        </Button>
        {rolePermissions?.includes("EJO::ESR::APPROVAL") &&
          pathSegments[1]?.includes("ejo-pending") && (
            <Button
              onClick={handleSubmit(onSubmit, onError)}
              disabled={
                hasErrors ||
                serviceRequestsArray.length === 0 ||
                updateEJO.isLoading
              }
              disableElevation
              variant="contained"
              sx={{ textTransform: "capitalize", borderRadius: 1.5 }}
            >
              {!updateEJO.isLoading ? "Approve" : null}
              {updateEJO.isLoading && "Approving..."}
            </Button>
          )}
        {rolePermissions?.includes("ESR::MANAGEMENT") &&
          pathSegments[1]?.includes("ejo-approved-and-esr-uncreated") && (
            <Button
              onClick={onSubmitESR}
              disabled={createESR.isLoading}
              disableElevation
              variant="contained"
              sx={{ textTransform: "capitalize", borderRadius: 1.5 }}
            >
              {!createESR.isLoading ? "Submit For Approval" : null}
              {createESR.isLoading && "Submitting..."}
            </Button>
          )}
      </Stack>
      <Divider color="black" />
      <Grid container spacing={2} sx={{ marginTop: "1em" }}>
        <Grid container spacing={2} sx={{ marginLeft: ".14em" }}>
          <Grid item sm={12} md={12} lg={6}>
            <AppTextField
              label="MRRF NO."
              labelAlignment="left"
              labelWidth="20%"
              fieldWidth="80%"
              fontWeight={400}
              placeholder="0"
              value={currentEJOSelected.ejoNumber}
              disabled={true}
            />
          </Grid>
          <Grid item sm={12} md={12} lg={6}>
            <Box>
              <AppTextField
                label="Business Unit"
                labelAlignment="left"
                labelWidth="20%"
                fieldWidth="80%"
                fontWeight={400}
                placeholder=""
                value={currentEJOSelected.businessUnit?.name}
                disabled={true}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <AppDatePicker
              name="dateCreated"
              value={currentEJOSelected.dateCreated ?? new Date()}
              onChange={() => {}}
              label="Date Created"
              labelAlignment="left"
              labelWidth="20%"
              fieldWidth="80%"
              fontWeight={400}
              disabled={true}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <Controller
              control={control}
              name="dateNeeded"
              render={({ field: { value, onChange } }) => (
                <AppDatePicker
                  name="dateNeeded"
                  value={new Date(value)}
                  onChange={onChange}
                  label="Date Needed"
                  labelAlignment="left"
                  labelWidth="20%"
                  fieldWidth="80%"
                  fontWeight={400}
                  error={!!errors.dateNeeded}
                  helperText={errors.dateNeeded?.message}
                  minDate={new Date()}
                  disabled={isViewOnly}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <Controller
              control={control}
              name="equipmentId"
              render={({ field }) => (
                <AppSelect
                  label="Equipment No."
                  labelAlignment="left"
                  labelWidth="20%"
                  fieldWidth="80%"
                  fontWeight={400}
                  placeholder="Select here"
                  error={!!errors.equipmentId}
                  helperText={errors.equipmentId?.message}
                  options={equipmentOptions}
                  {...register("equipmentId")}
                  {...field}
                  disabled={isViewOnly}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <Controller
              control={control}
              name="projectLocationId"
              render={({ field }) => (
                <AppSelect
                  label="Location"
                  labelAlignment="left"
                  labelWidth="20%"
                  fieldWidth="80%"
                  fontWeight={400}
                  placeholder="Select here"
                  options={locationOptions}
                  error={!!errors.projectLocationId}
                  helperText={errors.projectLocationId?.message}
                  {...register("projectLocationId")}
                  {...field}
                  disabled={isViewOnly}
                />
              )}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <AppTextField
              label="SMR/Mileage"
              labelAlignment="left"
              labelWidth="20%"
              fieldWidth="80%"
              fontWeight={400}
              placeholder="0"
              error={!!errors.smrMileage}
              helperText={errors.smrMileage?.message}
              {...register("smrMileage")}
              disabled={isViewOnly}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <Controller
              control={control}
              name="operatorId"
              render={({ field }) => (
                <AppSelect
                  label="Operator"
                  labelAlignment="left"
                  labelWidth="20%"
                  fieldWidth="80%"
                  fontWeight={400}
                  placeholder="Select here"
                  error={!!errors.operatorId}
                  helperText={errors.operatorId?.message}
                  options={operatorOptions}
                  {...register("operatorId")}
                  {...field}
                  disabled={isViewOnly}
                  />
              )}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <Controller
              control={control}
              name="isOperable"
              render={({ field: { value, onChange } }) => (
                <Stack direction="row" alignContent="center">
                  <Box sx={{ width: "20%" }}>
                    {" "}
                    <Typography
                      color={appColors.formText}
                      whiteSpace="nowrap"
                      fontSize={15}
                      sx={{
                        paddingTop: ".5em",
                        marginRight: "2.5em",
                      }}
                    >
                      Operable?
                    </Typography>
                  </Box>
                  <Box sx={{ width: "80%", display: "flex" }}>
                    <Switch
                      checked={Boolean(getValues().isOperable)}
                      {...register("isOperable")}
                      value={value}
                      onChange={onChange}
                      disabled={isViewOnly}
                    />
                    <Typography
                      color={appColors.formText}
                      whiteSpace="nowrap"
                      fontSize={15}
                      sx={{
                        paddingTop: ".5em",
                      }}
                    >
                      {!value ? "No" : "Yes"}
                    </Typography>
                  </Box>
                </Stack>
              )}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <EJOSRTable />
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <AppTextField
              label="Created By"
              labelAlignment="left"
              labelWidth="20%"
              fieldWidth="80%"
              fontWeight={400}
              placeholder=" "
              value={ejo?.ejoCreatorFirstName + " " + ejo?.ejoCreatorLastName}
              disabled={true}
            />
          </Box>
        </Grid>
        <Grid item sm={12} md={12} lg={6}>
          <Box>
            <Controller
              control={control}
              name="assigneeId"
              render={({ field }) => (
                <AppSelect
                  label="PIC"
                  labelAlignment="left"
                  labelWidth="20%"
                  fieldWidth="80%"
                  fontWeight={400}
                  placeholder="Select here"
                  options={picOptions}
                  error={!!errors.assigneeId}
                  helperText={errors.assigneeId?.message}
                  {...register("assigneeId")}
                  {...field}
                  disabled={isViewOnly}
                />
              )}
            />
          </Box>
        </Grid>
      </Grid>
      <EJOSRManagementForm equipmentModel={equipmentModel} />
      <EJOConfirmDialog
        openClearSrDialog={openClearSrDialog}
        setOpenClearSrDialog={setOpenClearSrDialog}
        handleSubmit={onProceedEquipmentAction}
        handleClose={onCancelEquipmentAction}
      />
      <EJOSRConfirmDeleteDialog />
    </>
  );
};

export default EJOApprovalForm;
